import React from "react";
import { truncateText } from "../../../utils/formatters";

export interface ReviewPendingInlineValueProps {
  /** When true and not editing, show strikethrough old + new instead of children (if old ≠ new). */
  pendingReviewDiffActive: boolean;
  /** Hide pending diff UI while the user is editing this section (or line item modal open). */
  isEditing?: boolean;
  oldText: string;
  newText: string;
  children: React.ReactNode;
  className?: string;
  /** Tailwind classes for the struck-through value (default: body bold). */
  oldValueClassName?: string;
  /** Tailwind classes for the new value (default: body bold). */
  newValueClassName?: string;
  /**
   * Character limit for pending-diff display. Defaults to 15 so narrow table cells
   * always show `...` while keeping the full value accessible via hover.
   */
  maxLength?: number;

  /**
   * Layout for pending diff display.
   * - "inline": old + new on one line (default)
   * - "stacked": old on first line, new on next line
   */
  layout?: "inline" | "stacked";
}

/**
 * Single-line pending-review display: ~~old~~ new (no arrow). Falls back to `children` when inactive or no diff.
 */
export const ReviewPendingInlineValue: React.FC<
  ReviewPendingInlineValueProps
> = ({
  pendingReviewDiffActive,
  isEditing = false,
  oldText,
  newText,
  children,
  className = "",
  oldValueClassName = "line-through opacity-70 text-body-bold font-body-bold text-default-font",
  newValueClassName = "text-body-bold font-body-bold text-default-font",
  maxLength = 15,
  layout = "inline",
}) => {
  const hasDiff = oldText !== newText;
  if (!pendingReviewDiffActive || isEditing || !hasDiff) {
    return <>{children}</>;
  }

  const oldFull = oldText.trim() === "" ? "—" : oldText;
  const newFull = newText.trim() === "" ? "—" : newText;
  const oldDisplay = truncateText(oldFull, maxLength);
  const newDisplay = truncateText(newFull, maxLength);

  return (
    <span
      className={`inline-flex min-w-0 max-w-full overflow-hidden ${
        layout === "stacked"
          ? "flex-col items-start gap-y-0.5"
          : "flex-nowrap items-baseline gap-x-1"
      } ${className}`}
      title={`${oldFull} → ${newFull}`}
    >
      <span
        className={`min-w-0 truncate whitespace-nowrap ${oldValueClassName}`}
      >
        {oldDisplay}
      </span>
      <span
        className={`min-w-0 truncate whitespace-nowrap ${newValueClassName}`}
      >
        {newDisplay}
      </span>
    </span>
  );
};

export interface ReviewFieldDiffHintProps {
  oldText: string;
  newText: string;
  /** Optional; omit to show a single line without a label row */
  label?: string;
  className?: string;
}

/**
 * Stacked supplemental diff (e.g. extra line fields). No arrow — old struck through, then new.
 */
export const ReviewFieldDiffHint: React.FC<ReviewFieldDiffHintProps> = ({
  oldText,
  newText,
  label,
  className = "",
}) => {
  if (oldText === newText) return null;
  return (
    <div
      className={`mt-0.5 flex max-w-full flex-col gap-0.5 text-caption font-caption text-subtext-color ${className}`}
    >
      {label ? (
        <span className="text-caption-bold font-caption-bold text-subtext-color">
          {label}
        </span>
      ) : null}
      <span className="break-words">
        <span className="line-through opacity-70">
          {oldText.trim() === "" ? "—" : oldText}
        </span>{" "}
        <span className="font-medium text-brand-700">
          {newText.trim() === "" ? "—" : newText}
        </span>
      </span>
    </div>
  );
};

export default ReviewFieldDiffHint;
